/**
 * The workspace's reserved attribute names.
 *
 * These are the names Customer.io owns on a person: the identifiers, the
 * timestamps it maintains, and the flags that drive sending. They are shared
 * rather than per-feature because the same list decides three different
 * things — whether an attribute renders with a "Reserved" badge, where it sorts
 * in a list of attributes, and whether a CSV column may be mapped onto it —
 * and a copy that fell behind would answer one of those questions differently
 * from the others.
 */

/** A person's timezone, and whether Customer.io could resolve it. */
export const TIMEZONE = 'timezone';
export const TIMEZONE_VALID = 'timezone_valid';

/** When Customer.io first saw the person, as opposed to the customer's own `created_at`. */
export const CREATED_IN_CIO_AT = '_created_in_customerio_at';

export const TEST_USER = 'cio_test_user';
export const TEST_USER_DOUBLE_OPT_IN = 'cio_double_opt_in';

/** Per-person open-tracking consent, which the send path reads (AUD-3987). */
export const CIO_EMAIL_TRACKING_CONSENT = 'cio_email_tracking_consent';

export const RESTRICTED_ATTRIBUTES: readonly string[] = [
  'id',
  'email',
  CREATED_IN_CIO_AT,
  'anonymous_id',
  'created_at',
  'email_sha256',
  'mobile_ad_id',
  'mobile_ad_id_sha256',
  'unsubscribed',
  TEST_USER,
  TEST_USER_DOUBLE_OPT_IN,
  CIO_EMAIL_TRACKING_CONSENT,
  TIMEZONE,
  TIMEZONE_VALID,
];

/**
 * Whether the name is one Customer.io reserves.
 *
 * Asked of a mapping's *current* target rather than its saved one wherever the answer decides
 * what gets written: a column re-pointed at a reserved attribute in this session is as reserved
 * as one that was saved that way.
 */
export function isRestrictedAttribute(name: string | null | undefined): boolean {
  return name != null && RESTRICTED_ATTRIBUTES.includes(name);
}

/** Reserved names on an event rather than a person. */
export const RESTRICTED_EVENT_ATTRIBUTES: readonly string[] = ['from_address', 'reply_to', 'recipient', 'form_submit'];

/**
 * Names an import hides from the mapping step until someone edits the row, so
 * they are not offered as ordinary columns. `id` is reserved as well; the name
 * this list adds on its own is the event timestamp.
 */
export const PROTECTED_ATTRIBUTES: readonly string[] = ['id', '_timestamp'];

/**
 * The three columns an event import needs, under the names the preprocessor
 * gives them. Everything else in an event row is its payload.
 */
export const REQUIRED_EVENT_IMPORT_ATTRIBUTES = {
  name: '_cio_name',
  customer_id: '_cio_customer_id',
  timestamp: '_cio_timestamp',
} as const;
